import React, { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Upload, FileText, X, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt', 'rtf'];
const MAX_FILES = 500;

export const ResumeUploadZone = ({ onUpload, isUploading, uploadProgress }) => {
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  const validateFile = (file) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return { valid: false, error: `Invalid format: ${ext}` };
    }
    if (file.size > 10 * 1024 * 1024) {
      return { valid: false, error: 'File too large (max 10MB)' };
    }
    return { valid: true };
  };

  const addFiles = useCallback((newFiles) => {
    const fileArray = Array.from(newFiles);
    
    if (files.length + fileArray.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const validatedFiles = fileArray.map(file => {
      const validation = validateFile(file);
      return {
        file,
        name: file.name,
        size: file.size,
        valid: validation.valid,
        error: validation.error,
      };
    });

    const existingNames = new Set(files.map(f => f.name));
    const uniqueFiles = validatedFiles.filter(f => !existingNames.has(f.name));
    
    if (validatedFiles.length !== uniqueFiles.length) {
      toast.warning('Some duplicate files were skipped');
    }

    setFiles(prev => [...prev, ...uniqueFiles]);
  }, [files]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
  };

  const handleUpload = () => {
    const validFiles = files.filter(f => f.valid).map(f => f.file);
    if (validFiles.length === 0) {
      toast.error('No valid files to upload');
      return;
    }
    onUpload(validFiles);
  };

  const validCount = files.filter(f => f.valid).length;
  const invalidCount = files.filter(f => !f.valid).length;

  return (
    <Card className="border-2 border-dashed border-primary/20" data-testid="resume-upload-zone">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Upload className="h-5 w-5 text-primary" strokeWidth={1.5} />
          Upload Resumes
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Drop Zone */}
        <div
          className={`
            relative p-8 border-2 border-dashed rounded-xl text-center cursor-pointer
            transition-all duration-200
            ${dragActive 
              ? 'border-primary bg-primary/5' 
              : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50'
            }
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('resume-file-input')?.click()}
          data-testid="resume-dropzone"
        >
          <input
            id="resume-file-input"
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.rtf"
            className="hidden"
            onChange={handleFileInput}
            data-testid="resume-file-input"
          />
          
          <div className="flex flex-col items-center gap-3">
            <div className={`p-4 rounded-full ${dragActive ? 'bg-primary/10' : 'bg-muted'}`}>
              <Upload className={`h-8 w-8 ${dragActive ? 'text-primary' : 'text-muted-foreground'}`} strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-medium">
                {dragActive ? 'Drop files here' : 'Drag & drop resumes here'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                or click to browse (PDF, Word, TXT, RTF)
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Up to {MAX_FILES} files, max 10MB each
              </p>
            </div>
          </div>
        </div>

        {/* File Stats */}
        {files.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="gap-1">
                <FileText className="h-3 w-3" />
                {files.length} files
              </Badge>
              {validCount > 0 && (
                <Badge className="gap-1 bg-green-500/10 text-green-500 border-green-500/20">
                  <CheckCircle2 className="h-3 w-3" />
                  {validCount} valid
                </Badge>
              )}
              {invalidCount > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {invalidCount} invalid
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={clearAll} data-testid="clear-files-btn">
              Clear All
            </Button>
          </div>
        )}

        {/* File List - Now fully scrollable */}
        {files.length > 0 && (
          <ScrollArea className="h-48 pr-2" data-testid="file-list-scroll">
            <div className="space-y-1">
              {files.map((file, idx) => (
                <div
                  key={idx}
                  className={`
                    flex items-center justify-between p-2 rounded-md text-sm
                    ${file.valid ? 'bg-muted/30' : 'bg-destructive/10'}
                  `}
                >
                  <div className="flex items-center gap-2 truncate flex-1 min-w-0">
                    <FileText className={`h-4 w-4 shrink-0 ${file.valid ? 'text-muted-foreground' : 'text-destructive'}`} />
                    <span className="truncate">{file.name}</span>
                    {!file.valid && (
                      <span className="text-xs text-destructive shrink-0">({file.error})</span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(idx);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Uploading...</span>
              <span className="font-medium">{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        )}

        {/* Upload Button */}
        {files.length > 0 && validCount > 0 && !isUploading && (
          <Button 
            className="w-full" 
            onClick={handleUpload}
            disabled={isUploading}
            data-testid="upload-resumes-btn"
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload {validCount} Resume{validCount !== 1 ? 's' : ''}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ResumeUploadZone;
